import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'

export interface CompanySettings {
  defaultCurrency: string
  currencySymbol: string
  timezone: string
  dateFormat: string
  timeFormat: string
  weekStart: number
  fiscalYearStart: string
  defaultLocale: string
  primaryColor: string
}

/**
 * Currency symbol, date format and timezone, from the company's own settings row.
 *
 * <p>There is deliberately **no fallback**. This used to default to the reference design's
 * Bangladeshi taka when the call failed, which meant a UAE deployment rendered ৳ against
 * dirham figures and nothing anywhere said that was a guess — a wrong currency symbol on a
 * payslip is not a cosmetic fault. When the settings cannot be loaded, `settings` is
 * undefined, money renders as a plain number with no symbol, and the shell shows a banner
 * saying so.
 */
export function useCompanySettings() {
  const query = useQuery({
    queryKey: ['settings', 'company'],
    queryFn: async () => {
      const { data } = await apiClient.get<CompanySettings>('/v1/settings/localization')
      return data
    },
    // Settings change a few times a year; refetching them per page would be waste.
    staleTime: 60 * 60 * 1000,
    retry: 1,
  })

  return {
    settings: query.data,
    isLoading: query.isLoading,
    /** True when the call has finished and produced nothing. The banner reads this. */
    unavailable: !query.isLoading && query.data === undefined,
  }
}
